import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Flame, Lock, Bell, X } from "lucide-react";
import { crmApi, crmUnlock, crmLogout, getCrmToken, getCrmProfile } from "@/lib/crm";
import { PulseTab, CustomersTab, OrdersTab, TasksTab, RecoveryTab } from "@/components/crm/CoreTabs";
import { MessagesTab, AutomationsTab, CopilotTab, InventoryTab, ReviewsTab } from "@/components/crm/CommsTabs";
import { SocialTab, GrowthTab } from "@/components/crm/SocialTabs";
import { AiCommandTab, SeoTab, AiGrowthTab } from "@/components/crm/SeoTabs";
import { VisitorsTab, SecurityTab, TeamTab } from "@/components/crm/IntelTabs";
import { Btn } from "@/components/crm/kit";

const TABS = [
  ["pulse", "Pulse"],
  ["ai-command", "AI Command"],
  ["customers", "Customers"],
  ["orders", "Orders"],
  ["tasks", "Tasks"],
  ["recovery", "Recovery"],
  ["messages", "Messages"],
  ["automations", "Automations"],
  ["copilot", "Copilot"],
  ["social", "Social Hub"],
  ["growth", "Growth"],
  ["reviews", "Reviews"],
  ["seo", "SEO Lab"],
  ["ai-growth", "AI Growth"],
  ["visitors", "Visitor Intel"],
  ["inventory", "Inventory"],
  ["security", "Security"],
  ["team", "Team"],
];

const ROLE_AREAS = {
  sales: ["pulse", "ai-command", "customers", "orders", "tasks", "recovery", "messages", "automations", "copilot", "growth", "reviews"],
  support: ["pulse", "ai-command", "customers", "orders", "tasks", "messages"],
  marketing: ["pulse", "ai-command", "tasks", "automations", "copilot", "social", "growth", "seo", "ai-growth", "visitors", "reviews"],
  fulfillment: ["pulse", "ai-command", "orders", "tasks", "inventory"],
};

function visibleTabs(role) {
  if (!role || role === "owner") return TABS;
  return TABS.filter(([id]) => ROLE_AREAS[role]?.includes(id));
}

function Unlock({ onUnlock }) {
  const [mode, setMode] = useState("key");
  const [key, setKey] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totp, setTotp] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await crmUnlock({ key: mode === "key" ? key : undefined, email, password, totp });
      onUnlock();
    } catch (err) {
      setError(err.response?.data?.detail || "Unlock failed");
    }
    setBusy(false);
  };
  return (
    <div className="min-h-screen bg-[#08090d] text-white flex items-center justify-center px-6" data-testid="crm-unlock">
      <motion.form
        onSubmit={submit}
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="card-glow-border rounded-3xl p-10 max-w-sm w-full bg-[#0f1117]"
      >
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-[#f97316] to-[#ea580c] shadow-[0_0_40px_rgba(255,98,31,0.5)] mb-6">
          <Lock size={22} className="text-white" strokeWidth={2.2} />
        </div>
        <h1 className="font-display font-bold text-2xl mb-1">NOXWAY CRM</h1>
        <p className="text-xs text-slate-500 mb-7">Staff only. Sessions last 8 hours.</p>
        {mode === "staff" ? (
          <>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Work email"
              required
              data-testid="crm-email-input"
              className="w-full bg-white/5 border border-white/15 rounded-lg px-4 py-3 text-sm text-white mb-3 focus:border-[#f97316] outline-none placeholder:text-slate-500"
            />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              required
              data-testid="crm-password-input"
              className="w-full bg-white/5 border border-white/15 rounded-lg px-4 py-3 text-sm text-white mb-3 focus:border-[#f97316] outline-none placeholder:text-slate-500"
            />
          </>
        ) : (
          <input
            type="password"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="Master staff key"
            required
            data-testid="crm-key-input"
            className="w-full bg-white/5 border border-white/15 rounded-lg px-4 py-3 text-sm text-white mb-3 focus:border-[#f97316] outline-none placeholder:text-slate-500"
          />
        )}
        <input
          value={totp}
          onChange={(e) => setTotp(e.target.value)}
          placeholder="Authenticator code (if enabled)"
          data-testid="crm-totp-input"
          className="w-full bg-white/5 border border-white/15 rounded-lg px-4 py-3 text-sm text-white mb-5 focus:border-[#f97316] outline-none placeholder:text-slate-500"
        />
        {error && <p className="text-xs text-red-400 mb-4" data-testid="crm-unlock-error">{error}</p>}
        <button
          type="submit"
          disabled={busy}
          data-testid="crm-unlock-button"
          className="w-full font-display font-bold tracking-[0.12em] text-sm px-8 py-3.5 rounded-full bg-gradient-to-r from-[#f97316] to-[#ea580c] text-white shadow-[0_8px_40px_rgba(255,98,31,0.4)] disabled:opacity-60"
        >
          {busy ? "CHECKING…" : "UNLOCK"}
        </button>
        <button
          type="button"
          onClick={() => setMode(mode === "staff" ? "key" : "staff")}
          data-testid="crm-unlock-mode-toggle"
          className="w-full text-center font-tech text-[10px] tracking-[0.15em] uppercase text-slate-500 hover:text-[#f97316] transition-colors mt-4"
        >
          {mode === "staff" ? "Use master key instead" : "Staff member? Sign in with email"}
        </button>
      </motion.form>
    </div>
  );
}

function SeoReviewPopup({ recs, onReview, onClose }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center px-5"
      data-testid="seo-review-popup"
    >
      <motion.div
        initial={{ scale: 0.94, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        className="card-glow-border rounded-3xl p-8 max-w-lg w-full bg-[#0f1117]"
      >
        <div className="flex items-start justify-between mb-5">
          <h3 className="font-display font-bold text-xl">{recs.length} SEO recommendation{recs.length > 1 ? "s" : ""} to review</h3>
          <button onClick={onClose} data-testid="seo-popup-close" className="text-slate-500 hover:text-white"><X size={18} /></button>
        </div>
        <ul className="space-y-2.5 mb-7 max-h-64 overflow-y-auto">
          {recs.slice(0, 6).map((r, i) => (
            <li key={r.id} className={`text-sm rounded-xl px-4 py-3 border ${i === 0 ? "border-[#f97316]/50 bg-[#f97316]/10" : "border-white/10 bg-white/[0.03]"}`}>
              {r.title}
            </li>
          ))}
        </ul>
        <div className="flex flex-wrap gap-3">
          <Btn onClick={onReview} data-testid="seo-popup-review">Review in SEO Lab</Btn>
          <button
            onClick={() => { localStorage.setItem("noxway_seo_snooze", String(Date.now() + 24 * 3600 * 1000)); onClose(); }}
            data-testid="seo-popup-snooze"
            className="font-tech text-[10px] tracking-[0.15em] uppercase px-4 py-2 rounded-full border border-white/20 text-slate-300 hover:border-white/50 transition-colors"
          >
            Snooze 24h
          </button>
        </div>
        <p className="text-[10px] text-slate-600 mt-4">Closing never changes approval status — recommendations stay in the lab.</p>
      </motion.div>
    </motion.div>
  );
}

export default function CRM() {
  const [authed, setAuthed] = useState(!!getCrmToken());
  const [tab, setTab] = useState("pulse");
  const [pendingSeo, setPendingSeo] = useState([]);
  const [popupOpen, setPopupOpen] = useState(false);
  const profile = getCrmProfile();
  const tabs = visibleTabs(profile?.role);

  const checkSeo = useCallback(() => {
    if (!getCrmToken()) return;
    crmApi("/seo/recommendations", { params: { status: "recommended" } })
      .then((recs) => {
        setPendingSeo(recs);
        const snoozedUntil = Number(localStorage.getItem("noxway_seo_snooze") || 0);
        if (recs.length > 0 && Date.now() > snoozedUntil) setPopupOpen(true);
      })
      .catch(() => {});
  }, []);

  useEffect(() => { if (authed) checkSeo(); }, [authed, checkSeo]);

  if (!authed) return <Unlock onUnlock={() => setAuthed(true)} />;

  const tabProps = { setTab };
  const panels = {
    "pulse": <PulseTab />,
    "ai-command": <AiCommandTab {...tabProps} />,
    "customers": <CustomersTab />,
    "orders": <OrdersTab />,
    "tasks": <TasksTab />,
    "recovery": <RecoveryTab />,
    "messages": <MessagesTab />,
    "automations": <AutomationsTab />,
    "copilot": <CopilotTab />,
    "reviews": <ReviewsTab />,
    "social": <SocialTab />,
    "growth": <GrowthTab />,
    "seo": <SeoTab />,
    "ai-growth": <AiGrowthTab />,
    "visitors": <VisitorsTab />,
    "inventory": <InventoryTab />,
    "security": <SecurityTab />,
    "team": <TeamTab />,
  };

  return (
    <div className="min-h-screen bg-[#08090d] text-white" data-testid="crm-app">
      <header className="sticky top-0 z-40 bg-[#08090d]/90 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#f97316] to-[#ea580c] flex items-center justify-center">
              <Flame size={16} className="text-white" strokeWidth={2.4} />
            </span>
            <span className="font-display font-bold tracking-[0.18em] text-sm">NOXWAY CRM</span>
          </div>
          <div className="flex items-center gap-3">
            {profile && (
              <span className="hidden sm:inline-block font-tech text-[10px] tracking-[0.12em] uppercase text-slate-500" data-testid="crm-identity">
                {profile.name} · {profile.role}
              </span>
            )}
            <button
              onClick={() => setPopupOpen(true)}
              data-testid="seo-bell"
              className="relative w-9 h-9 rounded-full border border-white/15 flex items-center justify-center hover:border-[#f97316] transition-colors"
              aria-label="SEO notifications"
            >
              <Bell size={15} />
              {pendingSeo.length > 0 && (
                <span className="absolute -top-1 -right-1 w-4.5 h-4.5 min-w-4 px-1 rounded-full bg-[#ea580c] text-[9px] font-bold flex items-center justify-center" data-testid="seo-bell-count">
                  {pendingSeo.length}
                </span>
              )}
            </button>
            <a href="/" className="font-tech text-[10px] tracking-[0.15em] text-slate-400 hover:text-[#f97316] transition-colors uppercase">Storefront</a>
            <button
              onClick={() => { crmLogout(); setAuthed(false); }}
              data-testid="crm-lock-button"
              className="font-tech text-[10px] tracking-[0.15em] uppercase px-4 py-2 rounded-full border border-white/20 text-slate-300 hover:border-white/50 transition-colors"
            >
              Lock
            </button>
          </div>
        </div>
        <nav className="max-w-7xl mx-auto px-5 lg:px-8 pb-3 flex gap-2 overflow-x-auto" data-testid="crm-tabs">
          {tabs.map(([id, label]) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              data-testid={`crm-tab-${id}`}
              className={`font-tech text-[10px] tracking-[0.12em] uppercase px-3.5 py-1.5 rounded-full whitespace-nowrap transition-colors ${
                tab === id ? "bg-[#f97316] text-black font-bold" : "border border-white/15 text-slate-400 hover:border-[#f97316]/50 hover:text-white"
              }`}
            >
              {label}
            </button>
          ))}
        </nav>
      </header>

      <main className="max-w-7xl mx-auto px-5 lg:px-8 py-8">
        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3 }}
          >
            {panels[tab]}
          </motion.div>
        </AnimatePresence>
      </main>

      <AnimatePresence>
        {popupOpen && pendingSeo.length > 0 && (
          <SeoReviewPopup
            recs={pendingSeo}
            onReview={() => { setTab("seo"); setPopupOpen(false); }}
            onClose={() => setPopupOpen(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
